import { useEffect, useState } from "react";
import { FileEdit, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useConfirm } from "./ConfirmModal";

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" }) + " · " +
           d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function stripHtml(html) {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  // Skip quoted-block content from replies so the preview shows the rep's text
  div.querySelectorAll(".gmail_quote").forEach(n => n.remove());
  return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
}

/**
 * Inline drafts panel — shown at the top of Lead/Deal timelines.
 * Lists the current user's saved drafts scoped to (linkedType, linkedId).
 *
 * Props:
 *  - linkedType, linkedId    filter scope
 *  - refreshKey              bump to force reload
 *  - onResume(draft)         parent opens ComposeEmailModal with this draft
 *  - onChanged()             fired after delete so parent can bump refreshKey
 */
export default function EmailDraftsPanel({ linkedType, linkedId, refreshKey = 0, onResume, onChanged }) {
  const confirm = useConfirm();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  const load = async () => {
    if (!linkedType || !linkedId) { setDrafts([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/email-drafts", { params: { linked_type: linkedType, linked_id: linkedId } });
      setDrafts(data.items || []);
    } catch {
      setDrafts([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [linkedType, linkedId, refreshKey]);

  // Cross-component draft-change bus: MovesTimeline's composer may create
  // drafts for the same entity — this keeps the panel in sync without
  // requiring shared React state between sibling timelines.
  useEffect(() => {
    const onBus = () => load();
    window.addEventListener("pragma:drafts-changed", onBus);
    return () => window.removeEventListener("pragma:drafts-changed", onBus);
    // eslint-disable-next-line
  }, [linkedType, linkedId]);

  const remove = async (d) => {
    const ok = await confirm({
      title: "Discard this draft?",
      message: `${d.subject || "(no subject)"} — this cannot be undone.`,
      destructive: true,
      confirmLabel: "Discard",
    });
    if (!ok) return;
    try {
      await api.delete(`/email-drafts/${d.id}`);
      toast.success("Draft discarded");
      onChanged?.();
      load();
    } catch {
      toast.error("Failed to discard");
    }
  };

  if (!linkedType || !linkedId) return null;
  if (!loading && drafts.length === 0) return null;

  return (
    <div
      className="pg-card p-4 mb-4"
      data-testid="email-drafts-panel"
      style={{ background: "var(--pg-surface-soft, rgba(91,79,232,0.04))", border: "1px dashed var(--pg-border)" }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%",
          background: "transparent", border: "none", padding: 0, cursor: "pointer",
          color: "var(--pg-text)",
        }}
        data-testid="email-drafts-toggle"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <FileEdit size={14} style={{ color: "var(--pg-purple)" }} />
        <span className="text-sm" style={{ fontWeight: 600 }}>
          Drafts <span className="text-mute" style={{ fontWeight: 400 }}>· {drafts.length}</span>
        </span>
      </button>

      {open && !loading && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          {drafts.map((d) => (
            <div
              key={d.id}
              className="rounded-md"
              style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "10px 12px",
                background: "var(--pg-surface)", border: "1px solid var(--pg-border)",
              }}
              data-testid={`email-draft-item-${d.id}`}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="text-sm truncate" style={{ color: "var(--pg-text)" }}>
                  <strong>{d.subject || "(no subject)"}</strong>
                  {d.reply_context && (
                    <span
                      className="mono text-[10px]"
                      style={{
                        marginLeft: 8, padding: "1px 6px", borderRadius: 4,
                        background: "rgba(91,79,232,0.10)", color: "var(--pg-purple)",
                        border: "1px solid rgba(91,79,232,0.25)",
                      }}
                    >Reply</span>
                  )}
                </div>
                <div className="text-xs mt-0.5 truncate" style={{ color: "var(--pg-text-mute)" }}>
                  {d.to ? `To ${d.to}` : "No recipient yet"}
                  {stripHtml(d.body_html) ? ` · ${stripHtml(d.body_html).slice(0, 80)}` : ""}
                </div>
                <div className="mono text-[11px] text-mute mt-1">
                  Saved {fmtWhen(d.updated_at)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button
                  type="button"
                  className="pg-btn"
                  style={{ padding: "4px 10px", fontSize: 12 }}
                  onClick={() => onResume?.(d)}
                  data-testid={`email-draft-resume-${d.id}`}
                >
                  Continue
                </button>
                <button
                  type="button"
                  className="pg-btn"
                  style={{ padding: "4px 8px", color: "var(--pg-red)" }}
                  onClick={() => remove(d)}
                  aria-label="Delete draft"
                  data-testid={`email-draft-delete-${d.id}`}
                  title="Discard draft"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
